import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart, faUser, faPhone, faMapMarkerAlt, faCalendarAlt, faBox, faRupeeSign, faTag,
    faExclamationTriangle, faPlus, faTrash, faSave, faTimes, faTrashAlt, faSpinner, faCheckCircle,
    faSearch, faBarcode, faMoneyBill, faPercent, faBoxes, faToggleOn, faToggleOff, faCopy,
    faStore, faBuilding, faFileAlt, faCalculator, faChevronDown
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// API Endpoints & Constants
// ==============================================
const SELL_API = 'https://ourzestapp.online/oz_backend/solar_module/solar_sell.php';
const CUSTOMER_API = 'https://ourzestapp.online/oz_backend/mechanics_module/mechanics_customers.php';
const PRODUCT_API = 'https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php';

const parseJsonResponse = async (response, endpointName = 'API') => {
    const body = await response.text();
    if (!body.trim()) {
        throw new Error(`${endpointName} returned an empty response (HTTP ${response.status}).`);
    }
    let data;
    try {
        data = JSON.parse(body);
    } catch (error) {
        throw new Error(`${endpointName} returned invalid JSON: ${body.slice(0, 200)}`);
    }
    if (!response.ok) {
        throw new Error(data.message || `${endpointName} request failed`);
    }
    return data;
};

const computeAvailableStock = (product) => {
    return Math.round(parseFloat(product.quantity) || 0);
};

const getProductSellPrice = (product) => {
    if (product.sell_per_pack && parseFloat(product.sell_per_pack) > 0) {
        return Math.round(parseFloat(product.sell_per_pack));
    }
    if (product.sell_price_per_unit && parseFloat(product.sell_price_per_unit) > 0) {
        return Math.round(parseFloat(product.sell_price_per_unit));
    }
    return Math.round(parseFloat(product.sell_price) || 0);
};

const buildSaleItem = (product, currentShopId) => {
    const price = getProductSellPrice(product) || 0;
    const availableStock = computeAvailableStock(product);
    const isCentralized = product.is_product_centralized === 1;
    const ownerShopId = product.owner_shop_id || null;

    return {
        product_id: product.id,
        product_name: product.product_name || '',
        product_code: product.product_code || '',
        batch_no: product.batch_no || '',
        barcode: product.barcode || '',
        price: String(price),
        quantity: '1',
        available_stock: availableStock,
        quantity_alert: parseFloat(product.quantity_alert) || 5,
        total: String(Math.round(price * 1)),
        sell_method: product.sell_method || 'sub_packing',
        is_centralized: isCentralized ? 1 : 0,
        owner_shop_id: ownerShopId,
        is_owner_shop: ownerShopId == currentShopId,
        supplier_name: product.supplier_name || '',
        company: product.company || '',
        items_per_pack: parseFloat(product.items_per_pack) || 1,
        unit_of_measure: product.unit_of_measure || 'piece'
    };
};

// ==============================================
// GLOBAL BARCODE SCANNER HOOK
// ==============================================
const useBarcodeScanner = (onBarcodeScanned, isActive = true) => {
    const bufferRef = useRef('');
    const lastTimeRef = useRef(0);
    const scanTimerRef = useRef(null);
    const isProcessingRef = useRef(false);
    const isLikelyScanningRef = useRef(false);
    const leakSnapshotRef = useRef(null);

    useEffect(() => {
        if (!isActive) return;

        const SCAN_MAX_INTERVAL_MS = 40;
        const MIN_BARCODE_LENGTH = 4;
        const BUFFER_TIMEOUT_MS = 150;

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        const setNativeValue = (el, value) => {
            if (!el) return;
            if (el.tagName === 'INPUT' && nativeInputValueSetter) {
                nativeInputValueSetter.call(el, value);
            } else {
                el.value = value;
            }
            el.dispatchEvent(new Event('input', { bubbles: true }));
        };

        const restoreLeak = () => {
            const snap = leakSnapshotRef.current;
            if (snap && snap.element && snap.element.isConnected) {
                setNativeValue(snap.element, snap.value);
                if (snap.selectionStart != null && snap.element.setSelectionRange) {
                    try {
                        snap.element.setSelectionRange(snap.selectionStart, snap.selectionStart);
                    } catch (e) { }
                }
            }
            leakSnapshotRef.current = null;
        };

        const resetBuffer = () => {
            bufferRef.current = '';
            isLikelyScanningRef.current = false;
            leakSnapshotRef.current = null;
        };

        const handleKeyDown = (e) => {
            const key = typeof e?.key === 'string' ? e.key : '';
            if (!key || e.ctrlKey || e.metaKey || e.altKey) {
                resetBuffer();
                return;
            }
            if (key === 'Shift' || key === 'CapsLock' || key === 'Tab') return;

            const now = Date.now();
            const delta = now - lastTimeRef.current;
            lastTimeRef.current = now;

            const target = e.target;
            const isEditable = !!target && (
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.isContentEditable
            );

            if (key === 'Enter') {
                clearTimeout(scanTimerRef.current);
                const candidate = String(bufferRef.current || '').trim();
                const isConfirmedScan = isLikelyScanningRef.current && candidate.length >= 4;

                if (isConfirmedScan) {
                    e.preventDefault();
                    e.stopPropagation();
                    restoreLeak();

                    if (!isProcessingRef.current) {
                        isProcessingRef.current = true;
                        onBarcodeScanned(candidate);
                        setTimeout(() => { isProcessingRef.current = false; }, 500);
                    }
                }
                resetBuffer();
                return;
            }

            if (key.length !== 1) {
                resetBuffer();
                return;
            }

            if (String(bufferRef.current || '').length === 0 || delta > SCAN_MAX_INTERVAL_MS) {
                bufferRef.current = key;
                isLikelyScanningRef.current = false;
                leakSnapshotRef.current = isEditable ? {
                    element: target,
                    value: target.value,
                    selectionStart: target.selectionStart
                } : null;
            } else {
                bufferRef.current = String(bufferRef.current || '') + key;
                isLikelyScanningRef.current = true;
                if (isEditable) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }

            clearTimeout(scanTimerRef.current);
            scanTimerRef.current = setTimeout(resetBuffer, BUFFER_TIMEOUT_MS);
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            clearTimeout(scanTimerRef.current);
        };
    }, [onBarcodeScanned, isActive]);
};

// ==================== TOAST NOTIFICATION ====================
const ToastNotification = ({ toast, setToast }) => {
    if (!toast.show) return null;
    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationTriangle} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{toast.type.toUpperCase()}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==================== ITEMS TABLE COMPONENT (DESKTOP + MOBILE CARDS) ====================
const ItemsTable = ({
    items, 
    onItemChange, 
    onSelectProductForRow, 
    onRemove, 
    onAdd, 
    errors, 
    disabled, 
    searchProductsAPI, 
    showToast, 
    currentShop,
    globalSearchTerm = '',
    onGlobalSearchChange = () => {},
    filteredProducts = [],
    showGlobalDropdown = false,
    onSelectProduct = () => {},
    isSearching = false
}) => {
    const [filteredByIndex, setFilteredByIndex] = useState({});
    const [searchTerms, setSearchTerms] = useState({});
    const [showDropdown, setShowDropdown] = useState({});
    const [searching, setSearching] = useState({});
    const rowRefs = useRef({});
    const searchDebounceRef = useRef({});
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(rowRefs.current).forEach(index => {
                if (rowRefs.current[index] && !rowRefs.current[index].contains(event.target)) {
                    setShowDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, []);

    const handleRowSearch = (index, term) => {
        setSearchTerms(prev => ({ ...prev, [index]: term }));
        if (searchDebounceRef.current[index]) clearTimeout(searchDebounceRef.current[index]);
        if (!term || term.trim().length < 2) {
            setFilteredByIndex(prev => ({ ...prev, [index]: [] }));
            setShowDropdown(prev => ({ ...prev, [index]: false }));
            return;
        }
        searchDebounceRef.current[index] = setTimeout(async () => {
            setSearching(prev => ({ ...prev, [index]: true }));
            const results = await searchProductsAPI(term);
            setFilteredByIndex(prev => ({ ...prev, [index]: results }));
            setShowDropdown(prev => ({ ...prev, [index]: results.length > 0 }));
            setSearching(prev => ({ ...prev, [index]: false }));
        }, 250);
    };

    const selectProduct = (index, product) => {
        onSelectProductForRow(index, product);
        setSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setFilteredByIndex(prev => ({ ...prev, [index]: [] }));
        setShowDropdown(prev => ({ ...prev, [index]: false }));
        if (showToast) showToast('success', `"${product.product_name}" added to sale`);
    };

    return (
        <div className="ozui-items-table-container" style={{ width: '100%', maxWidth: '100%', overflow: 'visible' }}>
            <div className="ozui-table-header-actions" style={{ width: '100%', flexWrap: 'wrap', gap: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faBoxes} /> Sale Items ({items.length})
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flex: '1 1 auto', justifyContent: 'flex-end' }}>
                    <div style={{ position: 'relative', flex: '1 1 250px', minWidth: '180px', maxWidth: '400px' }}>
                        <input
                            type="text"
                            value={globalSearchTerm}
                            onChange={(e) => onGlobalSearchChange(e.target.value)}
                            onFocus={() => {
                                if (globalSearchTerm && globalSearchTerm.length >= 2) {
                                    // Trigger search on focus
                                }
                            }}
                            placeholder="Search products by name, barcode, batch..."
                            disabled={disabled}
                            autoComplete="off"
                            style={{
                                width: '100%',
                                padding: '8px 12px 8px 36px',
                                border: '1px solid var(--ozui-border)',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                backgroundColor: 'var(--ozui-bg)',
                                color: 'var(--ozui-text)',
                                outline: 'none',
                                height: '38px',
                                boxSizing: 'border-box'
                            }}
                        />
                        <FontAwesomeIcon 
                            icon={faSearch} 
                            style={{ 
                                position: 'absolute', 
                                left: '10px', 
                                top: '50%', 
                                transform: 'translateY(-50%)',
                                color: 'var(--ozui-text-light)',
                                fontSize: '14px'
                            }} 
                        />
                        {isSearching && (
                            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                <FontAwesomeIcon icon={faSpinner} spin style={{ color: 'var(--ozui-text-light)' }} />
                            </span>
                        )}
                        {showGlobalDropdown && filteredProducts.length > 0 && (
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 2px)',
                                left: 0,
                                right: 0,
                                zIndex: 9999999,
                                backgroundColor: 'var(--ozui-bg)',
                                border: '1px solid var(--ozui-border)',
                                borderRadius: '8px',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                minWidth: '280px'
                            }}>
                                {filteredProducts.map(product => (
                                    <div
                                        key={product.id}
                                        onClick={() => onSelectProduct(product)}
                                        style={{
                                            padding: '10px 14px',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid var(--ozui-border-light)',
                                            transition: 'background-color 0.15s ease',
                                            backgroundColor: 'var(--ozui-bg)',
                                            color: 'var(--ozui-text)'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--ozui-surface)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--ozui-bg)'}
                                    >
                                        <div style={{
                                            fontWeight: '600',
                                            fontSize: '0.88rem',
                                            color: 'var(--ozui-text)',
                                            marginBottom: '4px',
                                            wordBreak: 'break-word'
                                        }}>
                                            {product.product_name}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            fontSize: '0.72rem',
                                            color: 'var(--ozui-text-light)',
                                            flexWrap: 'wrap',
                                            marginBottom: '4px'
                                        }}>
                                            {product.batch_no && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <FontAwesomeIcon icon={faTag} style={{ fontSize: '10px' }} />
                                                    {product.batch_no}
                                                </span>
                                            )}
                                            {product.barcode && (
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <FontAwesomeIcon icon={faBarcode} style={{ fontSize: '10px' }} />
                                                    {product.barcode}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            gap: '16px',
                                            fontSize: '0.72rem',
                                            flexWrap: 'wrap'
                                        }}>
                                            <span style={{
                                                color: '#059669',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <FontAwesomeIcon icon={faRupeeSign} style={{ fontSize: '10px' }} />
                                                {' '}
                                                {getProductSellPrice(product)}
                                                {' '}
                                                <span style={{ fontWeight: '400', color: 'var(--ozui-text-light)' }}>
                                                    /{product.sell_method === 'sub_packing' ? 'unit' : 'pack'}
                                                </span>
                                            </span>
                                            <span style={{
                                                color: computeAvailableStock(product) <= (product.quantity_alert || 5) ? '#dc2626' : 'var(--ozui-text-light)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                Stock: {computeAvailableStock(product)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button type="button" className="ozui-btn-add-item" onClick={onAdd} disabled={disabled}>
                        <FontAwesomeIcon icon={faPlus} /><span>Add Item</span>
                    </button>
                </div>
            </div>

            {/* DESKTOP COMPACT DATA TABLE VIEW */}
            <div className="ozui-table-wrapper ozui-desktop-only" style={{ overflowX: 'auto' }}>
                <table className="ozui-items-table" style={{ minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th>Sr#</th>
                            <th>Batch</th>
                            <th>Product Name <span className="ozui-required">*</span></th>
                            <th>Company</th>
                            <th>Sell Price <span className="ozui-required">*</span></th>
                            <th>Qty <span className="ozui-required">*</span></th>
                            <th>Total</th>
                            <th>Stock</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const filteredForIndex = filteredByIndex[index] || [];
                            const showDrop = showDropdown[index] || false;
                            const searchTerm = searchTerms[index] !== undefined ? searchTerms[index] : item.product_name;

                            return (
                                <tr key={index}>
                                    <td><strong>{index + 1}</strong></td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input type="text" id={`dt_batch_${index}`} value={item.batch_no || ''} readOnly disabled placeholder=" " />
                                            <label htmlFor={`dt_batch_${index}`}><FontAwesomeIcon icon={faTag} /> Batch</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper" ref={el => rowRefs.current[index] = el}>
                                            <input
                                                type="text"
                                                id={`dt_product_${index}`}
                                                value={searchTerm}
                                                onChange={e => handleRowSearch(index, e.target.value)}
                                                onFocus={() => searchTerm && searchTerm.length >= 2 && setShowDropdown(prev => ({ ...prev, [index]: filteredForIndex.length > 0 }))}
                                                placeholder=" "
                                                disabled={disabled}
                                                autoComplete="off"
                                            />
                                            <label htmlFor={`dt_product_${index}`}><FontAwesomeIcon icon={faBox} /> Product Name</label>
                                            {showDrop && filteredForIndex.length > 0 && (
                                                <div className="ozui-product-dropdown-table">
                                                    {filteredForIndex.map(p => (
                                                        <div key={p.id} className="ozui-product-option" onClick={() => selectProduct(index, p)}>
                                                            <div className="ozui-product-name">{p.product_name}</div>
                                                            <div className="ozui-product-details">Batch: {p.batch_no} | Rs. {getProductSellPrice(p)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input type="text" id={`dt_company_${index}`} value={item.company || item.supplier_name || ''} readOnly disabled placeholder=" " />
                                            <label htmlFor={`dt_company_${index}`}><FontAwesomeIcon icon={faBuilding} /> Company</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="number"
                                                id={`dt_price_${index}`}
                                                value={item.price || ''}
                                                onChange={e => onItemChange(index, 'price', e.target.value)}
                                                step="1"
                                                min="0"
                                                placeholder=" "
                                                disabled={disabled}
                                            />
                                            <label htmlFor={`dt_price_${index}`}><FontAwesomeIcon icon={faRupeeSign} /> Sell Price</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="number"
                                                id={`dt_qty_${index}`}
                                                value={item.quantity || ''}
                                                onChange={e => onItemChange(index, 'quantity', e.target.value)}
                                                step="1"
                                                min="1"
                                                placeholder=" "
                                                disabled={disabled}
                                            />
                                            <label htmlFor={`dt_qty_${index}`}><FontAwesomeIcon icon={faBoxes} /> Qty</label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="text"
                                                id={`dt_total_${index}`}
                                                value={Math.round((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))}
                                                readOnly
                                                disabled
                                                placeholder=" "
                                                className="ozui-readonly-input ozui-total-display"
                                            />
                                            <label htmlFor={`dt_total_${index}`}><FontAwesomeIcon icon={faCalculator} /> Total</label>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="ozui-stock-badge">
                                            {Math.round(parseFloat(item.available_stock || 0))}
                                        </span>
                                    </td>
                                    <td>
                                        <button type="button" className="ozui-table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* MOBILE TOUCH CARD VIEW */}
            <div className="ozui-mobile-items-list ozui-mobile-only">
                {items.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--ozui-text-light)' }}>
                        No items added yet. Search and add products above.
                    </div>
                ) : (
                    items.map((item, index) => {
                        const filteredForIndex = filteredByIndex[index] || [];
                        const showDrop = showDropdown[index] || false;
                        const searchTerm = searchTerms[index] !== undefined ? searchTerms[index] : item.product_name;

                        return (
                            <div key={index} className="ozui-mobile-item-card">
                                <div className="ozui-mobile-card-header">
                                    <span className="ozui-mobile-sr">#{index + 1}</span>
                                    <span className="ozui-mobile-total">Total: Rs. {Math.round((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0))}</span>
                                    <button
                                        type="button"
                                        className="ozui-mobile-remove-btn"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled || items.length === 1}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>

                                <div className="ozui-mobile-card-body">
                                    <div className="ozui-field" ref={el => rowRefs.current[`mob_${index}`] = el}>
                                        <div className="ozui-input-wrapper">
                                            <input
                                                type="text"
                                                id={`mob_prod_${index}`}
                                                value={searchTerm}
                                                onChange={e => handleRowSearch(index, e.target.value)}
                                                onFocus={() => searchTerm && searchTerm.length >= 2 && setShowDropdown(prev => ({ ...prev, [index]: filteredForIndex.length > 0 }))}
                                                placeholder=" "
                                                disabled={disabled}
                                                autoComplete="off"
                                            />
                                            <label htmlFor={`mob_prod_${index}`}><FontAwesomeIcon icon={faBox} /> Product Name <span>*</span></label>
                                            {showDrop && filteredForIndex.length > 0 && (
                                                <div className="ozui-product-dropdown-table">
                                                    {filteredForIndex.map(p => (
                                                        <div key={p.id} className="ozui-product-option" onClick={() => selectProduct(index, p)}>
                                                            <div className="ozui-product-name">{p.product_name}</div>
                                                            <div className="ozui-product-details">Batch: {p.batch_no} | Rs. {getProductSellPrice(p)}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="ozui-mobile-grid-2">
                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input type="text" id={`mob_batch_${index}`} value={item.batch_no || ''} readOnly disabled placeholder=" " />
                                                <label htmlFor={`mob_batch_${index}`}><FontAwesomeIcon icon={faTag} /> Batch</label>
                                            </div>
                                        </div>

                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input type="text" id={`mob_comp_${index}`} value={item.company || item.supplier_name || ''} readOnly disabled placeholder=" " />
                                                <label htmlFor={`mob_comp_${index}`}><FontAwesomeIcon icon={faBuilding} /> Company</label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ozui-mobile-grid-3">
                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input
                                                    type="number"
                                                    id={`mob_price_${index}`}
                                                    value={item.price || ''}
                                                    onChange={e => onItemChange(index, 'price', e.target.value)}
                                                    step="1"
                                                    min="0"
                                                    placeholder=" "
                                                    disabled={disabled}
                                                />
                                                <label htmlFor={`mob_price_${index}`}><FontAwesomeIcon icon={faRupeeSign} /> Price <span>*</span></label>
                                            </div>
                                        </div>

                                        <div className="ozui-field">
                                            <div className="ozui-input-wrapper">
                                                <input
                                                    type="number"
                                                    id={`mob_qty_${index}`}
                                                    value={item.quantity || ''}
                                                    onChange={e => onItemChange(index, 'quantity', e.target.value)}
                                                    step="1"
                                                    min="1"
                                                    placeholder=" "
                                                    disabled={disabled}
                                                />
                                                <label htmlFor={`mob_qty_${index}`}><FontAwesomeIcon icon={faBoxes} /> Qty <span>*</span></label>
                                            </div>
                                        </div>

                                        <div className="ozui-field">
                                            <span className="ozui-stock-badge">
                                                Stock: {Math.round(parseFloat(item.available_stock || 0))}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

const createSaleSessionId = () => {
    if (globalThis.crypto?.randomUUID) {
        return globalThis.crypto.randomUUID().replace(/-/g, '');
    }
    return (`sale_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`).slice(0, 32);
};

// ==================== MAIN SALES COMPONENT ====================
const ElectronicsSell = ({ saleId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();
    const [formSessionId] = useState(createSaleSessionId);

    const initialFormData = {
        date: new Date().toISOString().split('T')[0], 
        bn: '',
        customer_name: '', 
        customer_phone: '', 
        customer_address: '', 
        customer_id: null,
        subtotal: '0', 
        tax: '0', 
        discount: '0', 
        grand_total: '0',
        paid_amount: '0', 
        balance: '0', 
        payment_method: 'cash', 
        payment_status: 'unpaid',
        notes: '', 
        items: []
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ourzest_darkMode') === 'true');
    const [toast, setToast] = useState({ show: false, type: '', message: '' });

    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);

    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);

    const [globalSearchTerm, setGlobalSearchTerm] = useState('');
    const [filteredGlobalProducts, setFilteredGlobalProducts] = useState([]);
    const [showGlobalDropdown, setShowGlobalDropdown] = useState(false);
    const [searchingGlobalProducts, setSearchingGlobalProducts] = useState(false);

    const [isScannerActive, setIsScannerActive] = useState(true);

    const toastTimerRef = useRef(null);
    const customerSearchRef = useRef(null);
    const isSavingRef = useRef(false);
    const draftsDropdownRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => setToast({ show: false, type: '', message: '' }), 3000);
    }, []);

    // ==============================================
    // DEFINED FIRST - addProductToItems
    // ==============================================
    const addProductToItems = useCallback((product) => {
        if (!product || !product.id) return;

        setFormData(prev => {
            const existingIndex = prev.items.findIndex(item => item.product_id === product.id);
            const availableStock = computeAvailableStock(product);

            let newQty = 1;
            let updatedItems = [...prev.items];

            if (existingIndex >= 0) {
                const existing = updatedItems[existingIndex];
                newQty = (parseFloat(existing.quantity) || 0) + 1;

                if (availableStock > 0 && newQty > availableStock) {
                    showToast('error', 'Cannot add more than available stock');
                    return prev;
                }

                updatedItems[existingIndex] = {
                    ...existing,
                    quantity: String(newQty),
                    total: String(Math.round((parseFloat(existing.price) || 0) * newQty))
                };
            } else {
                const newItem = buildSaleItem(product, currentShop?.shop_id);
                updatedItems = [...prev.items, newItem];
            }

            return { ...prev, items: updatedItems };
        });

        setGlobalSearchTerm('');
        setShowGlobalDropdown(false);
    }, [showToast, currentShop?.shop_id]);

    // ==============================================
    // searchGlobalProducts
    // ==============================================
    const searchGlobalProducts = useCallback(async (term) => {
        if (!term || term.length < 2 || !user?.username || !currentShop?.shop_id) {
            setFilteredGlobalProducts([]);
            setShowGlobalDropdown(false);
            return;
        }
        setSearchingGlobalProducts(true);
        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    action: 'search_products', 
                    searchTerm: term, 
                    username: user.username, 
                    shop_id: currentShop.shop_id 
                })
            });
            const data = await parseJsonResponse(response, 'Products API');
            if (data.status === 'success') {
                const products = (data.data || []).map(p => ({
                    id: p.id,
                    product_name: p.product_name,
                    product_code: p.product_code || '',
                    batch_no: p.batch_no || '',
                    barcode: p.barcode || '',
                    sell_price: Math.round(parseFloat(p.sell_price) || 0),
                    sell_price_per_unit: Math.round(parseFloat(p.sell_price_per_unit) || 0),
                    sell_per_pack: p.sell_per_pack ? Math.round(parseFloat(p.sell_per_pack)) : null,
                    quantity: Math.round(parseFloat(p.quantity) || 0),
                    quantity_alert: Math.round(parseFloat(p.quantity_alert) || 5),
                    supplier_name: p.supplier_name || '',
                    company: p.company || '',
                    sell_method: p.sell_method || 'sub_packing',
                    items_per_pack: parseFloat(p.items_per_pack) || 1,
                    unit_of_measure: p.unit_of_measure || 'piece'
                }));
                setFilteredGlobalProducts(products);
                setShowGlobalDropdown(products.length > 0);
            } else {
                setFilteredGlobalProducts([]);
                setShowGlobalDropdown(false);
            }
        } catch (error) {
            console.error('Product search error:', error);
            setFilteredGlobalProducts([]);
            setShowGlobalDropdown(false);
        } finally {
            setSearchingGlobalProducts(false);
        }
    }, [user?.username, currentShop?.shop_id]);

    // ==============================================
    // handleSelectProduct (uses addProductToItems)
    // ==============================================
    const handleSelectProduct = useCallback((product) => {
        addProductToItems(product);
        setGlobalSearchTerm('');
        setShowGlobalDropdown(false);
        showToast('success', `"${product.product_name}" added to sale`);
    }, [addProductToItems, showToast]);

    // ==============================================
    // fetchProductsBySearch
    // ==============================================
    const fetchProductsBySearch = useCallback(async (term) => {
        if (!term || term.length < 2 || !user?.username || !currentShop?.shop_id) return [];
        try {
            const response = await fetch(PRODUCT_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'search_products', searchTerm: term, username: user.username, shop_id: currentShop.shop_id })
            });
            const data = await parseJsonResponse(response, 'Products API');
            if (data.status === 'success') {
                return (data.data || []).map(p => ({
                    id: p.id,
                    product_name: p.product_name,
                    product_code: p.product_code || '',
                    batch_no: p.batch_no || '',
                    barcode: p.barcode || '',
                    sell_price: Math.round(parseFloat(p.sell_price) || 0),
                    sell_price_per_unit: Math.round(parseFloat(p.sell_price_per_unit) || 0),
                    sell_per_pack: p.sell_per_pack ? Math.round(parseFloat(p.sell_per_pack)) : null,
                    quantity: Math.round(parseFloat(p.quantity) || 0),
                    quantity_alert: Math.round(parseFloat(p.quantity_alert) || 5),
                    supplier_name: p.supplier_name || '',
                    company: p.company || '',
                    sell_method: p.sell_method || 'sub_packing',
                    items_per_pack: parseFloat(p.items_per_pack) || 1,
                    unit_of_measure: p.unit_of_measure || 'piece'
                }));
            }
            return [];
        } catch (error) { return []; }
    }, [user?.username, currentShop?.shop_id]);

    // ==============================================
    // handleBarcodeScan (uses addProductToItems)
    // ==============================================
    const handleBarcodeScan = useCallback(async (barcode) => {
        if (!barcode || barcode.trim() === '') return;
        try {
            const results = await fetchProductsBySearch(barcode);
            if (results && results.length > 0) {
                const product = results[0];
                addProductToItems(product);
                showToast('success', `${product.product_name} found!`);
            } else {
                showToast('warning', `Product not found: ${barcode}`);
            }
        } catch (error) { showToast('error', 'Error scanning barcode'); }
    }, [fetchProductsBySearch, addProductToItems, showToast]);

    // Initialize barcode scanner
    useBarcodeScanner(handleBarcodeScan, isScannerActive);

    // ==============================================
    // useEffect hooks
    // ==============================================
    useEffect(() => {
        const sync = () => {
            const dark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(dark);
            document.body.classList.toggle('ozui-dark', dark);
            document.documentElement.classList.toggle('ozui-dark', dark);
        };
        sync();
        window.addEventListener('storage', sync);
        window.addEventListener('darkModeToggled', sync);
        return () => {
            window.removeEventListener('storage', sync);
            window.removeEventListener('darkModeToggled', sync);
        };
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (customerSearchRef.current && !customerSearchRef.current.contains(e.target)) {
                setShowCustomerDropdown(false);
            }
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(e.target)) {
                setShowDraftsList(false);
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setShowCustomerDropdown(false);
                setShowDraftsList(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // ==============================================
    // Drafts Logic
    // ==============================================
    const getDraftStorageKey = () => {
        const userId = user?.user_id || user?.username || 'anonymous';
        const shopId = currentShop?.shop_id || 'no_shop';
        return `sell_draft_${userId}_${shopId}`;
    };

    const loadDrafts = useCallback(() => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        setSavedDrafts(currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts);
    }, [currentShop, user]);

    useEffect(() => { loadDrafts(); }, [loadDrafts]);

    const saveDraft = () => {
        const hasData = (Array.isArray(formData.items) && formData.items.length > 0) || formData.customer_name?.trim();
        if (!hasData) { showToast('warning', 'Cannot save empty draft'); return; }

        const draft = { id: Date.now(), ...formData, savedAt: new Date().toISOString(), shop_id: currentShop?.shop_id };
        const existing = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        existing.unshift(draft);
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(existing.slice(0, 20)));
        showToast('success', 'Draft saved!');
        loadDrafts();
    };

    const loadDraft = (draft) => {
        setFormData({ ...initialFormData, ...draft, date: draft.date || new Date().toISOString().split('T')[0], items: draft.items || [] });
        setShowDraftsList(false);
        showToast('success', 'Draft loaded!');
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem(getDraftStorageKey()) || '[]');
        localStorage.setItem(getDraftStorageKey(), JSON.stringify(drafts.filter(d => d.id !== draftId)));
        loadDrafts();
        showToast('success', 'Draft deleted!');
    };

    const clearDraft = () => {
        setFormData(initialFormData);
        setGlobalSearchTerm('');
        setFilteredGlobalProducts([]);
        setShowGlobalDropdown(false);
        showToast('success', 'Form cleared');
    };

    // ==============================================
    // Auto-calculate Totals
    // ==============================================
    useEffect(() => {
        let subtotal = 0;
        formData.items.forEach(item => {
            const price = Math.round(parseFloat(item.price) || 0);
            const quantity = Math.round(parseFloat(item.quantity) || 0);
            subtotal += price * quantity;
        });
        const discount = Math.round(parseFloat(formData.discount) || 0);
        const grandTotal = Math.max(0, subtotal - discount);
        const paid = Math.round(parseFloat(formData.paid_amount) || 0);
        const balance = Math.max(0, grandTotal - paid);

        setFormData(prev => ({
            ...prev,
            subtotal: String(subtotal),
            grand_total: String(grandTotal),
            balance: String(balance)
        }));
    }, [formData.items, formData.discount, formData.paid_amount]);

    // ==============================================
    // Handlers
    // ==============================================
    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        const item = { ...updatedItems[index] };
        if (field === 'price' || field === 'quantity') {
            item[field] = value;
            const price = Math.round(parseFloat(item.price) || 0);
            const qty = Math.round(parseFloat(item.quantity) || 0);
            item.total = String(price * qty);
        } else {
            item[field] = value;
        }
        updatedItems[index] = item;
        setFormData(prev => ({ ...prev, items: updatedItems }));
    };

    const handleRowProductSelect = (index, product) => {
        const newItem = buildSaleItem(product, currentShop?.shop_id);
        setFormData(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], ...newItem };
            return { ...prev, items };
        });
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { 
                product_id: '', 
                product_name: '', 
                price: '0', 
                quantity: '1', 
                total: '0', 
                company: '',
                batch_no: '',
                barcode: '',
                available_stock: 0,
                sell_method: 'sub_packing'
            }]
        }));
    };

    const removeItem = (index) => {
        setFormData(prev => {
            const updated = [...prev.items];
            updated.splice(index, 1);
            return { ...prev, items: updated };
        });
    };

    const searchCustomers = async (term) => {
        if (!term || term.length < 2 || !currentShop?.shop_id) return;
        setSearchingCustomer(true);
        try {
            const response = await fetch(CUSTOMER_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_records', shop_id: currentShop.shop_id })
            });
            const data = await parseJsonResponse(response, 'Customers API');
            if (data.status === 'success' && data.data) {
                const filtered = data.data.filter(c =>
                    c.customer_name?.toLowerCase().includes(term.toLowerCase()) ||
                    c.phone?.includes(term)
                ).slice(0, 15);
                setCustomerSearchResults(filtered);
                setShowCustomerDropdown(filtered.length > 0);
            }
        } catch (e) { } finally { setSearchingCustomer(false); }
    };

    const selectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_id: customer.customerid,
            customer_name: customer.customer_name,
            customer_phone: customer.phone || '',
            customer_address: customer.address || ''
        }));
        setShowCustomerDropdown(false);
    };

    // ==============================================
    // HANDLE SUBMIT - FIXED API CALL
    // ==============================================
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSavingRef.current) return;
        if (!formData.bn) { showToast('error', 'BN Number is required'); return; }
        if (formData.items.length === 0) { showToast('error', 'Add at least one item'); return; }

        isSavingRef.current = true;
        setSaving(true);

        try {
            const payload = {
                action: 'add',
                username: user?.username,
                shop_id: currentShop?.shop_id,
                bn: formData.bn,
                date: formData.date,
                customer_name: formData.customer_name || 'Walk-in Customer',
                customer_phone: formData.customer_phone || '',
                customer_address: formData.customer_address || '',
                customer_id: formData.customer_id || null,
                subtotal: parseFloat(formData.subtotal) || 0,
                tax: 0,
                discount: parseFloat(formData.discount) || 0,
                grand_total: parseFloat(formData.grand_total) || 0,
                paid_amount: parseFloat(formData.paid_amount) || 0,
                payment_method: formData.payment_method || 'cash',
                notes: formData.notes || '',
                form_session_id: formSessionId,
                items: formData.items.map(item => ({
                    product_id: parseInt(item.product_id),
                    product_name: item.product_name || '',
                    batch_no: item.batch_no || '',
                    barcode: item.barcode || '',
                    price: parseFloat(item.price) || 0,
                    quantity: parseFloat(item.quantity) || 1,
                    sell_method: item.sell_method || 'sub_packing',
                    items_per_pack: parseFloat(item.items_per_pack) || 1,
                    unit_of_measure: item.unit_of_measure || 'piece',
                    company: item.company || '',
                    supplier_name: item.supplier_name || ''
                }))
            };

            console.log('Sending sale payload:', payload);

            const response = await fetch(SELL_API, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await parseJsonResponse(response, 'Sell API');

            if (result.status === 'success') {
                showToast('success', result.message || 'Sale completed successfully!');
                clearDraft();
                if (onSuccess) {
                    onSuccess(result);
                }
            } else {
                throw new Error(result.message || 'Failed to complete sale');
            }
        } catch (err) {
            console.error('Sale error:', err);
            showToast('error', err.message || 'Error processing sale');
        } finally {
            isSavingRef.current = false;
            setSaving(false);
        }
    };

    return (
        <div className={`ozui-container ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} />

            <div className="ozui-sticky-header">
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{isEditMode ? 'Edit Sale' : 'New Sale'}</h1>
                            <p>Sell Products & Issue Receipt</p>
                        </div>
                    </div>
                    <div className="ozui-header-right">
                        <div className="ozui-header-actions">
                            <button className="ozui-action-btn clear-draft" onClick={clearDraft} title="Clear Form">
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>Clear</span>
                            </button>

                            <button className="ozui-action-btn save-draft" onClick={saveDraft} title="Save Draft">
                                <FontAwesomeIcon icon={faCopy} />
                                <span>Save Draft</span>
                            </button>

                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button
                                    className="ozui-action-btn drafts-btn"
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title="View Drafts"
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>Drafts</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>Saved Drafts</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">No drafts saved</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">{draft.bn || draft.customer_name || `Draft #${draft.id}`}</div>
                                                            <div className="draft-details">{draft.customer_name}</div>
                                                        </div>
                                                        <button className="delete-draft-btn" onClick={(e) => { e.stopPropagation(); deleteDraft(draft.id); }}>
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button className="ozui-action-btn scanner-toggle" onClick={() => setIsScannerActive(!isScannerActive)}>
                                <FontAwesomeIcon icon={isScannerActive ? faToggleOn : faToggleOff} />
                                <span>{isScannerActive ? 'Scanner ON' : 'Scanner OFF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="ozui-form">
                <div className="ozui-form-row">
                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="bn"
                                name="bn"
                                value={formData.bn}
                                onChange={e => setFormData({ ...formData, bn: e.target.value })}
                                placeholder=" "
                                required
                            />
                            <label htmlFor="bn"><FontAwesomeIcon icon={faTag} /> BN Number <span>*</span></label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="date"
                                name="date"
                                id="date"
                                value={formData.date}
                                onChange={e => setFormData({ ...formData, date: e.target.value })}
                                placeholder=" "
                            />
                            <label htmlFor="date"><FontAwesomeIcon icon={faCalendarAlt} /> Date</label>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-row">
                    <div className="ozui-field" ref={customerSearchRef}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                id="customer_name"
                                name="customer_name"
                                value={formData.customer_name}
                                onChange={e => {
                                    setFormData({ ...formData, customer_name: e.target.value, customer_id: null });
                                    searchCustomers(e.target.value);
                                }}
                                onFocus={() => formData.customer_name.length >= 2 && setShowCustomerDropdown(true)}
                                placeholder=" "
                                autoComplete="off"
                            />
                            <label htmlFor="customer_name"><FontAwesomeIcon icon={faUser} /> Customer Name</label>
                        </div>
                        {showCustomerDropdown && customerSearchResults.length > 0 && (
                            <div className="ozui-customer-dropdown">
                                {customerSearchResults.map(c => (
                                    <div key={c.customerid} className="ozui-dropdown-item" onClick={() => selectCustomer(c)}>
                                        <div className="ozui-customer-name">{c.customer_name}</div>
                                        {c.phone && <div className="ozui-customer-phone"><FontAwesomeIcon icon={faPhone} /> {c.phone}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                name="customer_phone"
                                id="customer_phone"
                                value={formData.customer_phone}
                                onChange={e => setFormData({ ...formData, customer_phone: e.target.value })}
                                placeholder=" "
                            />
                            <label htmlFor="customer_phone"><FontAwesomeIcon icon={faPhone} /> Phone Number</label>
                        </div>
                    </div>
                </div>

                <ItemsTable
                    items={formData.items}
                    onItemChange={handleItemChange}
                    onSelectProductForRow={handleRowProductSelect}
                    onRemove={removeItem}
                    onAdd={addItem}
                    errors={errors}
                    disabled={saving}
                    searchProductsAPI={fetchProductsBySearch}
                    showToast={showToast}
                    currentShop={currentShop}
                    globalSearchTerm={globalSearchTerm}
                    onGlobalSearchChange={(term) => {
                        setGlobalSearchTerm(term);
                        searchGlobalProducts(term);
                    }}
                    filteredProducts={filteredGlobalProducts}
                    showGlobalDropdown={showGlobalDropdown}
                    onSelectProduct={handleSelectProduct}
                    isSearching={searchingGlobalProducts}
                />

                <div className="ozui-form-section">
                    <div className="ozui-calculations">
                        <div className="ozui-calculation-row">
                            <span className="ozui-calculation-label">Subtotal:</span>
                            <span className="ozui-calculation-value">Rs. {formData.subtotal}</span>
                        </div>

                        <div className="ozui-calculation-row">
                            <div className="ozui-input-wrapper ozui-calc-input-wrapper">
                                <input
                                    type="number"
                                    name="discount"
                                    id="discount_input"
                                    value={formData.discount}
                                    onChange={e => setFormData({ ...formData, discount: e.target.value })}
                                    step="1"
                                    min="0"
                                    placeholder=" "
                                />
                                <label htmlFor="discount_input">
                                    <FontAwesomeIcon icon={faPercent} /> Discount
                                </label>
                            </div>
                        </div>

                        <div className="ozui-calculation-row ozui-grand-total">
                            <span className="ozui-calculation-label">Grand Total:</span>
                            <span className="ozui-calculation-value">Rs. {formData.grand_total}</span>
                        </div>

                        <div className="ozui-calculation-row">
                            <div className="ozui-input-wrapper ozui-calc-input-wrapper">
                                <input
                                    type="number"
                                    name="paid_amount"
                                    id="paid_amount_input"
                                    value={formData.paid_amount}
                                    onChange={e => setFormData({ ...formData, paid_amount: e.target.value })}
                                    step="1"
                                    min="0"
                                    placeholder=" "
                                />
                                <label htmlFor="paid_amount_input">
                                    <FontAwesomeIcon icon={faMoneyBill} /> Paid Amount
                                </label>
                            </div>
                        </div>

                        <div className="ozui-calculation-row">
                            <span className="ozui-calculation-label">Balance:</span>
                            <span className="ozui-calculation-value">Rs. {formData.balance}</span>
                        </div>
                    </div>
                </div>

                <div className="ozui-form-actions">
                    <button type="submit" className="ozui-btn ozui-btn-primary" disabled={saving || formData.items.length === 0}>
                        {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                        <span>Save & Complete Sale</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ElectronicsSell;
export { ItemsTable as SaleItemsTable, useBarcodeScanner, buildSaleItem };